import { forEach, orderBy, find } from 'lodash';

import CoreUtils from '../../common/utils/CoreUtils';
import NumberUtils from '../../common/utils/NumberUtils';

import MetadataHelper from '../../common/value-types/MetadataHelper';

import ScheduleDataModel from '../../schedules/ScheduleDataModel';

import BasicPropertyHelper from '../../schedules/property-helpers/BasicPropertyHelper';

import CarbonFreePackageRuleRenderer from './CarbonFreePackageRuleRenderer';

const scheduleDateField = 'scheduleDate';
const isCarbonFreeField = 'isCarbonFree';

const cfDeliveredLabel = 'Total CF Delivered';
const cfPcp2AllocationLabel = 'Total CF Allocation (PCP 2.0)';
const cfZainetAllocationLabel = 'Total CF Allocation (Zainet)';

const annualCfQuantity = 'AnnualCfQuantity';

export default class CarbonFreeDataModel extends ScheduleDataModel {
  constructor(locale, counterparty, commandDataModel, summaryDataModel) {
    super(locale, counterparty, commandDataModel)

    this.summaryDataModel = summaryDataModel;
  }

  getDefaultSortCriteria() {
    return {
      fields: ['localDateMs', 'contractCode'],
      order: ['asc', 'asc']
    };
  }

  processFullServerUpdate(scheduleUpdateDto) {
    this.allowToggleCarbonFree = scheduleUpdateDto.allowToggleCarbonFree;

    super.processFullServerUpdate(scheduleUpdateDto);
  }

  processContracts(scheduleUpdateDto) {
    forEach(scheduleUpdateDto.contracts, contractDto => {
      var contract = {};
      Object.assign(contract, contractDto);
      
      this.registerContract(contract);
    });

    this.totalCFAllocation = null;
    this.totalCFDelivered = null;
    this.totalCFRemaining = null;
  }

  updateScheduleUsage(schedule, updatedDataFields) {
    super.updateScheduleUsage(schedule, updatedDataFields);

    var energySumMWh = 0;
    var energySumH = 0;

    var carbonFreeTotalMWh = 0;
    var carbonFreeTotalH = 0;

    var carbonFreePendingMWh = 0;
    var carbonFreePendingH = 0;

    var isCarbonFreeCurrent = schedule[isCarbonFreeField];

    var isCarbonFreeMetadataField = MetadataHelper.resolveMetadataField(isCarbonFreeField);
    var isCarbonFreeCommitted = schedule[isCarbonFreeMetadataField].originalValue;

    forEach(
      schedule.hourlyDataFields, dataField => {
        var metadataField = MetadataHelper.resolveMetadataField(dataField);

        var carbonFreeCurrentMWh = 0;
        var carbonFreeCurrentH = 0;

        var carbonFreeCommittedMWh = 0;
        var carbonFreeCommittedH = 0;

        var currentMWh = schedule[dataField].averageValue;
        var currentH = schedule[dataField].averageDuration;

        var committedMWh = schedule[metadataField].originalEnergyValue.averageValue;
        var committedH = schedule[metadataField].originalEnergyValue.averageDuration;

        if (isCarbonFreeCurrent) {
          carbonFreeCurrentMWh = currentMWh;
          carbonFreeCurrentH = currentH;
        }

        if (isCarbonFreeCommitted) {
          carbonFreeCommittedMWh = committedMWh;
          carbonFreeCommittedH = committedH;
        }

        energySumMWh += schedule[dataField].averageValue;
        energySumH += schedule[dataField].averageDuration;

        carbonFreeTotalMWh += carbonFreeCurrentMWh;
        carbonFreeTotalH += carbonFreeCurrentH;

        carbonFreePendingMWh += carbonFreeCurrentMWh - carbonFreeCommittedMWh;
        carbonFreePendingH += carbonFreeCurrentH - carbonFreeCommittedH;
      }
    );

    if (energySumMWh !== schedule.energySumMWh) {
      schedule.energySumMWh = energySumMWh;
      updatedDataFields.push('energySumMWh');
    }

    if (energySumH !== schedule.energySumH) {
      schedule.energySumH = energySumH;
      updatedDataFields.push('energySumH');
    }

    if (carbonFreeTotalMWh !== schedule.carbonFreeTotalMWh) {
      schedule.carbonFreeTotalMWh = carbonFreeTotalMWh;
      updatedDataFields.push('carbonFreeTotalMWh');
    }

    if (carbonFreeTotalH !== schedule.carbonFreeTotalH) {
      schedule.carbonFreeTotalH = carbonFreeTotalH;
      updatedDataFields.push('carbonFreeTotalH');
    }

    if (carbonFreePendingMWh !== schedule.carbonFreePendingMWh) {
      schedule.carbonFreePendingMWh = carbonFreePendingMWh;
      updatedDataFields.push('carbonFreePendingMWh');
    }

    if (carbonFreePendingH !== schedule.carbonFreePendingH) {
      schedule.carbonFreePendingH = carbonFreePendingH;
      updatedDataFields.push('carbonFreePendingH');
    }
  }

  updateOverallStatus() {
    super.updateOverallStatus();

    this.hasValidationErrors = this.allSchedules.some(
      schedule => schedule.hasValidationErrors
    );

    if (CoreUtils.isDefinedAndNotNull(this.commandDataModel)) {
      this.commandDataModel.updateProperties({
        hasValidationErrors: this.hasValidationErrors
      });
    }

    this.localCarbonFreeTotalMWh = 0;
    this.localCarbonFreePendingMWh = 0;

    CoreUtils.iterateProperties(this.indexedSchedules, (_, schedule) => {
      var contract = this.indexedContracts[schedule.contractCode];
      if (contract === undefined)
        return;

      this.localCarbonFreeTotalMWh += schedule.carbonFreeTotalMWh;
      this.localCarbonFreePendingMWh += schedule.carbonFreePendingMWh;
    });

    this.triggerSummaryUpdate();
  }

  processExternalData(externalDatas) {
    var triggerSummaryUpdate = false;

    var cfPcp2AllocationRecord = find(
      externalDatas,
      item => item.dataLabel === cfPcp2AllocationLabel
    );

    var cfZainetAllocationRecord = find(
      externalDatas,
      item => item.dataLabel === cfZainetAllocationLabel
    );

    var cfDeliveredRecord = find(
      externalDatas,
      item => item.dataLabel === cfDeliveredLabel
    );

    var annualCfQuantityParameter = this.counterparty.parameters[annualCfQuantity];
    if (CoreUtils.isUndefinedOrNull(annualCfQuantityParameter))
      annualCfQuantityParameter = '0';

    this.annualCfQuantity = parseInt(annualCfQuantityParameter);

    if (CoreUtils.isUndefinedOrNull(cfPcp2AllocationRecord)
      || CoreUtils.isUndefinedOrNull(cfZainetAllocationRecord)
      || CoreUtils.isUndefinedOrNull(cfDeliveredRecord))
      return;

    var cfPcp2Allocation = null;
    var cfZainetAllocation = null;

    var cfAllocation = null;
    var cfDelivered = null;
    var cfRemaining = null;

    if (cfPcp2AllocationRecord.computedValue !== undefined)
      cfPcp2Allocation = cfPcp2AllocationRecord.computedValue;

    if (cfZainetAllocationRecord.computedValue !== undefined)
      cfZainetAllocation = cfZainetAllocationRecord.computedValue;

    if (cfPcp2Allocation !== null && cfZainetAllocation !== null)
      cfAllocation = Math.max(cfPcp2Allocation, cfZainetAllocation);
    else if (cfPcp2Allocation !== null)
      cfAllocation = cfPcp2Allocation;
    else if (cfZainetAllocation !== null)
      cfAllocation = cfZainetAllocation;

    if (cfDeliveredRecord.computedValue !== undefined)
      cfDelivered = cfDeliveredRecord.computedValue;

    if (CoreUtils.isDefinedAndNotNull(cfAllocation) && CoreUtils.isDefinedAndNotNull(cfDelivered))
      cfRemaining = Math.max(0, cfAllocation - cfDelivered);

    if (this.totalCFAllocation !== cfAllocation) {
      this.totalCFAllocation = cfAllocation;
      triggerSummaryUpdate = true;
    }

    if (this.totalCFDelivered !== cfDelivered) {
      this.totalCFDelivered = cfDelivered;
      triggerSummaryUpdate = true;
    }

    if (this.totalCFRemaining !== cfRemaining) {
      this.totalCFRemaining = cfRemaining;
      triggerSummaryUpdate = true;
    }

    if (triggerSummaryUpdate)
      this.triggerSummaryUpdate();
  }

  triggerSummaryUpdate() {
    if (CoreUtils.isUndefinedOrNull(this.summaryDataModel))
      return;

    var contracts = [];
    CoreUtils.iterateProperties(this.indexedContracts, (_, contract) => {
      contracts.push(contract);
    });

    this.summaryDataModel.updateProperties({
      counterparty: this.counterparty,
      contracts: orderBy(contracts, ['code'], ['asc']),
      totalCFAllocation: this.totalCFAllocation,
      totalCFDelivered: this.totalCFDelivered,
      totalCFRemaining: this.totalCFRemaining
    });
  }

  establishEditFieldTriggers(editFieldTriggers) {
    super.establishEditFieldTriggers(editFieldTriggers);

    editFieldTriggers.push({
      triggerDataField: ScheduleDataModel.anyElectableHourFields,
      triggerValue: ScheduleDataModel.anyValue,
      updateDataField: ScheduleDataModel.allElectableHourFields,
      updateValue: ScheduleDataModel.sameValue
    });

    editFieldTriggers.push({
      triggerDataField: ScheduleDataModel.anyElectableHourFields,
      triggerValue: ScheduleDataModel.minimumValue,
      updateDataField: isCarbonFreeField,
      updateValue: false
    });

    editFieldTriggers.push({
      triggerDataField: ScheduleDataModel.anyElectableHourFields,
      triggerValue: ScheduleDataModel.aboveMinimumValue,
      updateDataField: isCarbonFreeField,
      updateValue: this.shouldSetIsCarbonFreeToTrue.bind(this)
    });
  }

  shouldSetIsCarbonFreeToTrue(schedule, contract) {
    var annualCfRemaining = CoreUtils.isDefinedAndNotNull(this.totalCFDelivered) && this.totalCFDelivered >= 0
      ? this.annualCfQuantity - this.totalCFDelivered
      : 0;

    annualCfRemaining -= this.localCarbonFreePendingMWh;

    return annualCfRemaining >= 1 || schedule[isCarbonFreeField];
  }

  isElectableHourField(schedule, dataField) {
    if (dataField === isCarbonFreeField)
      return this.allowToggleCarbonFree && schedule.enableEditValues;

    return super.isElectableHourField(schedule, dataField);
  }

  onFieldValuesEditing(schedule, dataFieldValues) {
    if (!super.onFieldValuesEditing(schedule, dataFieldValues))
      return false;

    return this.filterUpdatingElectableHourFields(
      schedule,
      dataFieldValues,
      this.indexedContracts[schedule.contractCode]
    );
  }

  onScheduleCreated(schedule) {
    super.onScheduleCreated(schedule);
  }

  onScheduleUpdated(schedule, isServerUpdate, isPeerUpdate, updatedDataFields, quickEntryHints) {
    super.onScheduleUpdated(schedule, isServerUpdate, isPeerUpdate, updatedDataFields, quickEntryHints);

    if (!isServerUpdate && !isPeerUpdate)
      this.enforceSchedulePackageValue(schedule, updatedDataFields, quickEntryHints);
  }

  onSchedulePropertiesCreating(schedule, contract, scheduleDto) {
    super.onSchedulePropertiesCreating(schedule, contract, scheduleDto);

    BasicPropertyHelper.onPropertyCreating(
      schedule,
      isCarbonFreeField,
      '',
      scheduleDto.metaValues[isCarbonFreeField],
      scheduleDto.firstUtcElectionCutoff
    );
  }

  onSchedulePropertiesServerUpdate(schedule, contract, scheduleDto, isPeerUpdate, updatedDataFields) {
    super.onSchedulePropertiesServerUpdate(schedule, contract, scheduleDto, isPeerUpdate, updatedDataFields);

    BasicPropertyHelper.onPropertyServerUpdate(
      schedule,
      isCarbonFreeField,
      scheduleDto.metaValues[isCarbonFreeField],
      true,
      isPeerUpdate,
      updatedDataFields,
      this.locale
    );
  }

  onSchedulePropertiesModifying(schedule, contract, scheduleUpdateSet, updatedDataFields) {
    super.onSchedulePropertiesModifying(schedule, contract, scheduleUpdateSet, updatedDataFields);

    BasicPropertyHelper.onPropertyModifying(
      schedule,
      isCarbonFreeField,
      schedule[isCarbonFreeField],
      scheduleUpdateSet,
      updatedDataFields,
      this.locale
    );
  }

  onSchedulePropertiesEditing(schedule, dataFieldHints, updatedDataFields) {
    super.onSchedulePropertiesEditing(schedule, dataFieldHints, updatedDataFields);

    BasicPropertyHelper.onPropertyEditing(
      schedule,
      isCarbonFreeField,
      dataFieldHints,
      updatedDataFields
    );
  }

  onSchedulePropertiesCutoffCheck(schedule, serverUtcTimeMs, warningPeriodMs, updatedDataFields) {
    super.onSchedulePropertiesCutoffCheck(schedule, serverUtcTimeMs, warningPeriodMs, updatedDataFields);

    BasicPropertyHelper.onPropertyCutoffCheck(
      schedule,
      isCarbonFreeField,
      updatedDataFields,
      serverUtcTimeMs,
      warningPeriodMs
    );
  }

  onSchedulePropertiesReverting(schedule, updatedDataFields) {
    super.onSchedulePropertiesReverting(schedule, updatedDataFields);

    BasicPropertyHelper.onPropertyReverting(
      schedule,
      isCarbonFreeField,
      updatedDataFields
    );
  }

  onSchedulePropertiesSerialize(schedule, scheduleSubmissionDto) {
    super.onSchedulePropertiesSerialize(schedule, scheduleSubmissionDto);

    scheduleSubmissionDto.metaValues[isCarbonFreeField] = schedule[isCarbonFreeField];
  }

  onSchedulesInitialized(schedules) {
    super.onSchedulesInitialized(schedules);

    schedules.forEach(
      schedule => this.determinePackageRuleFlags(
        schedule,
        scheduleDateField,
        (packageRule, otherDates) => CarbonFreePackageRuleRenderer(
          new Date(schedule.scheduleDate),
          packageRule.canConfigureIndependently,
          otherDates,
          'elected',
          this.locale
        )
      )
    );
  }

  enforceSchedulePackageValue(schedule, updatedDataFields, quickEntryHints) {
    var dateKey = ScheduleDataModel.generateDateKey(schedule.scheduleDate);
    var packageRule = this.indexedPackageRules[schedule.contractCode][dateKey];

    if (CoreUtils.isUndefinedOrNull(packageRule))
      return;

    if (packageRule.canConfigureIndependently)
      return;

    var packageDataFields = [];
    updatedDataFields.forEach(updatedDataField => {
      if (schedule.hourlyDataFields.indexOf(updatedDataField) !== -1
        || updatedDataField === isCarbonFreeField)
        packageDataFields.push(updatedDataField);
    })

    if (packageDataFields.length === 0)
      return;

    var scheduleUpdateSets = [];
    forEach(packageRule.packageDateKeys, packageDateKey => {
      var scheduleDateKey = ScheduleDataModel.generateScheduleDateKey(
        packageDateKey,
        schedule.contractCode
      );

      var packageSchedule = this.dateIndexedSchedules[scheduleDateKey];
      if (CoreUtils.isDefinedAndNotNull(packageSchedule) && packageSchedule.scheduleId !== schedule.scheduleId) {
        var values = {};
        packageDataFields.forEach(
          packageDataField => values[packageDataField] = schedule[packageDataField]
        );

        if (CoreUtils.getPropertyCount(values) > 0)
          scheduleUpdateSets.push({
            scheduleId: packageSchedule.scheduleId,
            values: values
          });
      }
    });

    if (scheduleUpdateSets.length > 0)
      this.updateSchedules(scheduleUpdateSets, false, true);
  }
}